import { ArrowLeft, ArrowRight } from 'lucide-react';

const VENDOR_COLORS = ['text-emerald-400', 'text-blue-400', 'text-purple-400'];
const VENDOR_BG     = ['bg-emerald-500',   'bg-blue-500',   'bg-purple-500'];

const FIELDS = [
  { field: 'name',        label: 'Company / Brand Name', required: true,  placeholder: 'e.g. Acme Corp' },
  { field: 'product_name',label: 'Product / Model Name', required: false, placeholder: 'e.g. Acme Pro 2026' },
  { field: 'website',     label: 'Website',              required: false, placeholder: 'https://…' },
  { field: 'product_url', label: 'Product URL',          required: false, placeholder: 'https://…/product' },
];

/**
 * Step 3 — Competitor name & URL entry.
 * @param {{ vendors, onUpdate, canProceed, onProceed, onBack }} props
 */
export function CompetitorEntry({ vendors, onUpdate, canProceed, onProceed, onBack }) {
  return (
    <div>
      <h2 className="text-base md:text-lg font-black text-white mb-2">Who Are You Comparing?</h2>
      <p className="text-slate-400 text-sm mb-8 leading-relaxed">
        The sponsored vendor is pre-filled. Enter the two competitors you want to evaluate.
        You'll rate all three on each feature you selected.
      </p>

      <div className="space-y-5">
        {vendors.map((v, idx) => (
          <div key={idx} data-testid={`vendor-card-${idx}`}
            className={`rounded-xl border p-5 ${v.is_sponsor ? 'border-amber-700/50 bg-amber-950/20' : 'border-slate-700 bg-slate-800'}`}>
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-3 h-3 rounded-full ${VENDOR_BG[idx]}`} />
              <span className={`font-semibold text-sm ${VENDOR_COLORS[idx]}`}>
                {idx === 0 ? 'Sponsored Vendor' : `Competitor ${idx}`}
              </span>
              {v.is_sponsor && <span className="text-xs bg-amber-950/60 text-amber-400 border border-amber-800/50 px-2 py-0.5 rounded font-semibold">Pre-filled</span>}
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {FIELDS.map(({ field, label, required, placeholder }) => (
                <div key={field}>
                  <label htmlFor={`vendor-${field}-${idx}`} className="text-xs text-slate-400 mb-1 block">
                    {label} {required && !v.is_sponsor && <span className="text-red-400">*</span>}
                  </label>
                  <input
                    id={`vendor-${field}-${idx}`}
                    data-testid={`vendor-${field}-${idx}`}
                    value={v[field] || ''}
                    onChange={e => onUpdate(idx, field, e.target.value)}
                    readOnly={v.is_sponsor}
                    className={`w-full bg-slate-700/80 border rounded-lg px-3 py-2 text-sm text-white transition-colors
                      ${v.is_sponsor ? 'border-slate-600 opacity-60 cursor-default' : 'border-slate-600 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500'}`}
                    placeholder={placeholder}
                  />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <p className="text-xs text-slate-500 mt-4">
        * Competitor names are required. Other fields help when you print or export.
      </p>

      <div className="flex items-center justify-between gap-3 mt-8">
        <button type="button" data-testid="competitors-back-button" onClick={onBack} className="text-slate-400 hover:text-white flex items-center gap-2 text-sm transition-colors">
          <ArrowLeft size={16} /> Back
        </button>
        <button type="button" data-testid="proceed-to-rating" disabled={!canProceed} onClick={onProceed}
          className={`px-6 py-3 rounded-xl font-semibold flex items-center gap-2 transition-all
            ${canProceed ? 'bg-emerald-600 hover:bg-emerald-500 text-white shadow-lg shadow-emerald-900/30' : 'bg-slate-700 text-slate-500 cursor-not-allowed'}`}>
          Start Rating <ArrowRight size={16} />
        </button>
      </div>
    </div>
  );
}
